<template>
  <div>
    <!-- ── Main View Dialog ── -->
    <v-dialog v-model="dialog" eager persistent scrollable fullscreen>
      <v-form ref="InternRequestForm" @submit.prevent>
        <v-card class="fullscreen-card">
          <!-- Header -->
          <div class="dialog-header">
            <span class="text--white">HTE Intern Request</span>
            <v-spacer></v-spacer>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="closeD()"
              class="close-btn"
            />
          </div>

          <v-divider></v-divider>

          <v-card-text class="fullscreen-body">
            <div class="view-layout">
              <!-- Left: Request Details -->
              <div class="details-panel">
                <div class="panel-section">
                  <div class="panel-section-title">
                    <v-icon size="15" color="#16a34a"
                      >mdi-information-outline</v-icon
                    >
                    Request Details
                  </div>

                  <div class="detail-row">
                    <span class="detail-label">Number of Interns</span>
                    <span class="detail-value highlight">{{
                      num_interns
                    }}</span>
                  </div>

                  <div class="detail-row" v-if="programSelected.length > 0">
                    <span class="detail-label">Programs</span>
                    <div class="tags-wrap">
                      <span
                        class="prog-tag"
                        v-for="id in programSelected"
                        :key="id"
                      >
                        {{ getProgramAbbr(id) }}
                      </span>
                    </div>
                  </div>

                  <div class="detail-row">
                    <span class="detail-label">Student Batch</span>
                    <div class="tags-wrap">
                      <span class="prog-tag">
                        {{ getBatch }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Skills -->
                <div
                  class="panel-section"
                  v-if="skillRequired && skillsList.length > 0"
                >
                  <div class="panel-section-title">
                    <v-icon size="15" color="#2563eb"
                      >mdi-lightbulb-outline</v-icon
                    >
                    Required Skills
                  </div>
                  <div class="skills-wrap">
                    <span
                      class="skill-tag"
                      v-for="i in skillsList"
                      :key="i.id"
                      :class="{ active: selectedSkills.includes(i.title) }"
                    >
                      {{ i.title }}
                    </span>
                  </div>
                </div>

                <!-- Requested Students -->
                <div class="panel-section" v-if="selectedStudents.length > 0">
                  <div class="panel-section-title">
                    <v-icon size="15" color="#7c3aed"
                      >mdi-account-group-outline</v-icon
                    >
                    Requested Students
                    <span class="section-badge">{{ getStudents.length }}</span>
                  </div>
                  <div class="student-list">
                    <div
                      class="student-list-row"
                      v-for="(name, idx) in getStudents"
                      :key="idx"
                    >
                      <div class="student-list-avatar">
                        {{ getInitials(name) }}
                      </div>
                      <span class="student-list-name">{{ name }}</span>
                      <span class="student-num">#{{ idx + 1 }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <div class="dialog-footer" v-if="status == 1">
            <v-btn class="footer-cancel-btn" elevation="0" @click="closeD()">
              Close
            </v-btn>
            <v-btn
              class="footer-submit-btn"
              elevation="0"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="requestAction('grant')"
            >
              <v-icon start size="16">mdi-check-circle</v-icon>
              Review Request
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- ── Remarks / Assignment Dialog ── -->
    <v-dialog
      v-model="remarksDialog"
      eager
      persistent
      scrollable
      max-width="760"
    >
      <v-form ref="RemarksRequestForm" @submit.prevent>
        <v-card class="dialog-card">
          <!-- Header -->
          <div class="dialog-header">
            <span class="text-white">
              {{
                requested_studentsList.length > 0
                  ? "Review Requested Students"
                  : "Assign Recommended Students"
              }}
            </span>
            <v-spacer></v-spacer>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click="closeRemarks()"
              class="close-btn"
            />
          </div>

          <v-divider></v-divider>

          <v-card-text class="remarks-body">
            <!-- Drag columns -->
            <div class="drag-columns">
              <!-- Left column: Requested / Recommended -->
              <div class="drag-col">
                <div class="drag-col-header green">
                  <v-icon size="16" color="#fff">mdi-account-check</v-icon>
                  <span>{{
                    requested_studentsList.length > 0
                      ? "Requested Students"
                      : "Recommended Students"
                  }}</span>
                  <span class="col-count">{{
                    (requested_studentsList.length > 0
                      ? requested_studentsList
                      : recommended_students
                    ).length
                  }}</span>
                </div>
                <draggable
                  class="drag-zone"
                  :list="
                    requested_studentsList.length > 0
                      ? requested_studentsList
                      : recommended_students
                  "
                  :group="{ name: 'my-group', put: true }"
                  drag-class="dragging"
                  chosen-class="chosen"
                  item-key="id"
                >
                  <template #item="{ element }">
                    <div class="drag-item">
                      <div class="drag-avatar green-av">
                        {{ element.fname[0] }}{{ element.lname[0] }}
                      </div>
                      <div class="drag-info">
                        <div class="drag-name">
                          {{ element.fname }} {{ element.lname }}
                        </div>
                        <div class="drag-status">
                          {{ getInternShipStatus(element.internship_status) }}
                        </div>
                      </div>
                      <v-icon size="14" color="#9ca3af" class="drag-handle"
                        >mdi-drag-vertical</v-icon
                      >
                    </div>
                  </template>
                  <template #footer>
                    <div
                      class="drag-empty"
                      v-if="
                        (requested_studentsList.length > 0
                          ? requested_studentsList
                          : recommended_students
                        ).length === 0
                      "
                    >
                      <v-icon size="24" color="#d1d5db"
                        >mdi-account-off-outline</v-icon
                      >
                      <span>No students here</span>
                    </div>
                  </template>
                </draggable>
              </div>

              <!-- Right column: Denied / To Assign -->
              <div class="drag-col">
                <div
                  class="drag-col-header"
                  :class="requested_studentsList.length > 0 ? 'red' : 'blue'"
                >
                  <v-icon size="16" color="#fff">{{
                    requested_studentsList.length > 0
                      ? "mdi-account-remove"
                      : "mdi-account-arrow-right"
                  }}</v-icon>
                  <span>{{
                    requested_studentsList.length > 0
                      ? "Denied Students"
                      : "Students to Assign"
                  }}</span>
                  <span class="col-count">{{ deniedList.length }}</span>
                </div>
                <draggable
                  class="drag-zone"
                  :list="deniedList"
                  :group="'my-group'"
                  item-key="id"
                >
                  <template #item="{ element }">
                    <div
                      class="drag-item"
                      :class="
                        requested_studentsList.length > 0 ? 'denied' : 'assign'
                      "
                    >
                      <div
                        class="drag-avatar"
                        :class="
                          requested_studentsList.length > 0
                            ? 'red-av'
                            : 'blue-av'
                        "
                      >
                        {{ element.fname[0] }}{{ element.lname[0] }}
                      </div>
                      <div class="drag-info">
                        <div class="drag-name">
                          {{ element.fname }} {{ element.lname }}
                        </div>
                        <div class="drag-status">
                          {{ getInternShipStatus(element.internship_status) }}
                        </div>
                      </div>
                      <v-icon size="14" color="#9ca3af" class="drag-handle"
                        >mdi-drag-vertical</v-icon
                      >
                    </div>
                  </template>
                  <template #footer>
                    <div class="drag-empty" v-if="deniedList.length === 0">
                      <v-icon size="24" color="#d1d5db"
                        >mdi-tray-arrow-down</v-icon
                      >
                      <span>Drag here</span>
                    </div>
                  </template>
                </draggable>
              </div>
            </div>

            <!-- Denied count notice -->
            <div class="denied-notice" v-if="deniedList.length > 0">
              <v-icon size="15" color="#d97706">mdi-alert-outline</v-icon>
              <span
                >{{ deniedList.length }} student(s) marked as denied — remarks
                are required.</span
              >
            </div>

            <!-- Remarks -->
            <div class="remarks-section">
              <div class="section-label">
                <v-icon size="15" color="#374151"
                  >mdi-comment-text-outline</v-icon
                >
                Remarks
                <span class="required-badge" v-if="deniedList.length > 0"
                  >Required</span
                >
                <span class="optional-badge" v-else>Optional</span>
              </div>
              <v-textarea
                v-model="remarks"
                :rules="deniedList.length > 0 ? [formRules.required] : []"
                placeholder="Add remarks about this request review..."
                :color="'#16a34a'"
                variant="outlined"
                density="compact"
                rows="3"
                hide-details="auto"
                class="remarks-field"
              ></v-textarea>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <div class="dialog-footer">
            <v-btn
              class="footer-cancel-btn"
              elevation="0"
              @click="closeRemarks()"
            >
              Cancel
            </v-btn>
            <v-btn
              class="footer-submit-btn"
              elevation="0"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="save()"
            >
              <v-icon start size="16">mdi-check-circle</v-icon>
              Save Review
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    ></fade-away-message-component>
  </div>
</template>

<script>
import eventBus from "@/eventBus";
import draggable from "vuedraggable";
export default {
  props: { data: Object, action: String },
  components: { draggable },
  data() {
    return {
      remarksDialog: false,
      dialog: false,
      requestActionType: null,
      btnLoading: false,
      name: null,
      programsList: [],
      batch_selected: null,
      batchList: [],
      programSelected: [],
      studentList: [],
      num_interns: null,
      skillRequired: false,
      skillsList: [],
      selectedStudents: [],
      selectedSkills: [],
      allowedStudents: [],
      requested_studentsList: [],
      status: null,
      remarks: null,
      itemSelected: null,
      deniedList: [],
      recommended_students: [],
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
    };
  },
  computed: {
    getStudents() {
      let req = this.data.st_data;
      let arr = [];
      for (let i = 0; i < req.length; i++) {
        let name = this.toTitleCase(
          req[i].fname +
            " " +
            (req[i].mname != "" && req[i].mname != null
              ? req[i].mname[0] + ". "
              : "") +
            req[i].lname,
        );
        arr.push(name);
      }
      return arr;
    },
    getBatch() {
      if (this.batch_selected) {
        const found = this.batchList.find(
          (item) => item.id == this.batch_selected,
        );
        return found ? found.name : "";
      }
      return "";
    },
  },
  watch: {
    data: {
      handler(data) {
        this.dialog = true;
        this.getProgsAndBatch();
        this.$refs.InternRequestForm.resetValidation();
        if (data.id) {
          this.selectedStudents = data.student_requested;
          this.selectedSkills = data.skills;
          this.programSelected = data.programs;
          this.num_interns = data.num_interns;
          this.status = data.status;
          this.allowedStudents = data.student_requested;
          this.batch_selected = data.student_batch_id;
          this.loadSkills();
        } else {
          this.$refs.InternRequestForm.reset();
        }
      },
      deep: true,
    },
  },
  methods: {
    getInitials(fullName) {
      const parts = fullName.trim().split(" ");
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : fullName[0].toUpperCase();
    },

    getProgramAbbr(id) {
      if (id) {
        const found = this.programsList.find((item) => item.id == id);
        return found ? found.abbr : "";
      }
      return "";
    },

    getRequestedStudents() {
      let arr = [];
      for (let i = 0; i < this.studentList.length; i++) {
        for (let j = 0; j < this.selectedStudents.length; j++) {
          if (this.selectedStudents[j] == this.studentList[i].id) {
            arr.push(this.studentList[i]);
          }
        }
      }
      this.requested_studentsList = arr;
    },

    getIsChecked(skill) {
      return this.selectedSkills.includes(skill);
    },

    loadSkills() {
      let progs = JSON.stringify(this.programSelected);
      if (this.programSelected.length > 0) {
        this.axiosCall(
          "/skill-assessement/getSkillCategoryByProgram/" + progs,
          "GET",
        ).then((res) => {
          if (res.data) {
            this.skillsList = res.data.data;
            this.skillRequired = res.data.isRequired;
            let data = res.data.students;
            data.forEach((element, i) => {
              data[i].fname = this.toTitleCase(element.fname);
              data[i].lname = this.toTitleCase(element.lname);
            });
            this.studentList = data;
          }
        });
      }
    },

    requestAction(type) {
      this.getRequestedStudents();
      this.requestActionType = type;
      this.deniedList = [];
      if (this.selectedStudents.length == 0) {
        this.loadStudentBasedOnRequest();
      }
      this.remarks = null;
      this.remarksDialog = true;
    },

    loadStudentBasedOnRequest() {
      this.axiosCall(
        "/student/find-intern/byRequest/" + this.data.id,
        "GET",
      ).then((res) => {
        if (res.data) this.recommended_students = res.data;
      });
    },

    async getProgsAndBatch() {
      try {
        const [progsRes, batchRes] = await Promise.all([
          this.axiosCall("/program", "GET"),
          this.axiosCall("/student/get-institute-batch-list", "GET"),
        ]);

        this.programsList = progsRes.data;
        this.batchList = batchRes.data;
      } catch (error) {
        console.error(error);
      }
    },

    closeRemarks() {
      this.requestActionType = null;
      this.remarksDialog = false;
    },

    getInternShipStatus(status) {
      const map = {
        null: "Available",
        1: "Requested",
        2: "Deployed",
        3: "Completed",
        4: "Terminated",
      };
      return map[status] ?? "Available";
    },

    async save() {
      let validate = await this.$refs.RemarksRequestForm.validate();
      if (validate.valid) {
        let data = {
          request_id: this.data.id,
          student_granted: JSON.stringify(this.requested_studentsList),
          student_denied: JSON.stringify(this.deniedList),
          remarks: this.remarks,
        };
        this.axiosCall("/organization/grant-intern-request", "POST", data).then(
          (res) => {
            if (res.data.status == 200) {
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "success";
              this.fadeAwayMessage.header = "System Message";
              this.fadeAwayMessage.message = res.data.msg;
              this.closeD();
            } else if (res.data.status == 400) {
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "error";
              this.fadeAwayMessage.header = "System Message";
              this.fadeAwayMessage.message = res.data.msg;
            }
          },
        );
      }
    },

    closeD() {
      eventBus.emit("closeCompanyInternRequestDialog", true);
      this.btnLoading = false;
      this.dialog = false;
      this.remarksDialog = false;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap");

/* ── Shared ── */
.dialog-card,
.fullscreen-card {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  border-radius: 16px !important;
  overflow: hidden;
}
.fullscreen-card {
  border-radius: 0 !important;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: #fff;
}
.dialog-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}
.close-btn {
  color: #9ca3af !important;
}

/* ── Footer ── */
.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  background: #fff;
}
.footer-cancel-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  background: #f3f4f6 !important;
  color: #374151 !important;
  border: 1px solid #e5e7eb !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 38px !important;
  padding: 0 18px !important;
}
.footer-submit-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
  background: #16a34a !important;
  color: #fff !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 38px !important;
  padding: 0 20px !important;
}

/* ── Main dialog body ── */
.fullscreen-body {
  background: #f4f6f8;
  padding: 28px !important;
  overflow-y: auto;
}
.view-layout {
  max-width: 640px;
  margin: 0 auto;
}

/* ── Details panel ── */
.details-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.panel-section {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.panel-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.section-badge {
  margin-left: auto;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  padding: 1px 8px;
}
.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f9fafb;
}
.detail-row:last-child {
  border-bottom: none;
}
.detail-label {
  font-size: 12px;
  color: #6b7280;
}
.detail-value {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.detail-value.highlight {
  background: #dcfce7;
  color: #15803d;
  padding: 2px 10px;
  border-radius: 6px;
  border: 1px solid #bbf7d0;
}

.tags-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.prog-tag {
  background: #dbeafe;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
}

.skills-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.skill-tag {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
}
.skill-tag.active {
  background: #dbeafe;
  color: #1d4ed8;
  border-color: #bfdbfe;
}

.student-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.student-list-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.student-list-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #ede9fe;
  color: #6d28d9;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.student-list-name {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.student-num {
  font-size: 11px;
  color: #9ca3af;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 1px 6px;
}

/* ── Remarks dialog body ── */
.remarks-body {
  padding: 20px !important;
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-height: 560px;
  overflow-y: auto;
  background: #f9fafb;
}

/* ── Drag columns ── */
.drag-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.drag-col {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}
.drag-col-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
}
.drag-col-header.green {
  background: #16a34a;
}
.drag-col-header.red {
  background: #dc2626;
}
.drag-col-header.blue {
  background: #2563eb;
}
.col-count {
  margin-left: auto;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 8px;
}

.drag-zone {
  min-height: 180px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.drag-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: grab;
  transition: box-shadow 0.15s ease;
}
.drag-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.drag-item.denied {
  background: #fff5f5;
  border-color: #fecaca;
}
.drag-item.assign {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.drag-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.drag-avatar.green-av {
  background: #dcfce7;
  color: #15803d;
}
.drag-avatar.red-av {
  background: #fee2e2;
  color: #dc2626;
}
.drag-avatar.blue-av {
  background: #dbeafe;
  color: #1d4ed8;
}

.drag-info {
  flex: 1;
}
.drag-name {
  font-size: 12px;
  font-weight: 600;
  color: #111827;
}
.drag-status {
  font-size: 11px;
  color: #6b7280;
  margin-top: 1px;
}
.drag-handle {
  cursor: grab;
}

.drag-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 24px 10px;
  font-size: 12px;
  color: #9ca3af;
}

/* drag states */
.dragging {
  opacity: 0.5;
}
.chosen {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12) !important;
  transform: scale(1.02);
}

/* ── Denied notice ── */
.denied-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 12px;
  color: #92400e;
  font-weight: 500;
}

/* ── Remarks section ── */
.remarks-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.required-badge {
  margin-left: 4px;
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 8px;
}
.optional-badge {
  margin-left: 4px;
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 8px;
}
.remarks-field :deep(.v-field) {
  border-radius: 8px !important;
  background: #fff !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
}
.remarks-field :deep(.v-field__outline__start),
.remarks-field :deep(.v-field__outline__end),
.remarks-field :deep(.v-field__outline__notch) {
  border-color: #d1d5db !important;
}
.remarks-field :deep(.v-field--focused .v-field__outline__start),
.remarks-field :deep(.v-field--focused .v-field__outline__end),
.remarks-field :deep(.v-field--focused .v-field__outline__notch) {
  border-color: #16a34a !important;
}
.remarks-field :deep(textarea) {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  color: #111827 !important;
}
</style>
